import React from 'react';

interface BreadcrumbItem {
  name: string;
  path: string;
  isLast: boolean;
}

interface BreadcrumbNavigationProps {
  truncatedBreadcrumbs: (BreadcrumbItem | { isEllipsis: true })[];
  currentPrefix: string;
  onNavigateToBreadcrumb: (path: string) => void;
  onGoBack: () => void;
  isLoading?: boolean;
}

const BreadcrumbNavigation: React.FC<BreadcrumbNavigationProps> = ({ 
  truncatedBreadcrumbs, 
  currentPrefix, 
  onNavigateToBreadcrumb, 
  onGoBack,
  isLoading = false
}) => {
  return (
    <div className="flex items-center justify-between mt-4 text-sm">
      {/* Breadcrumb trail with folder icon */}
      <div className="flex items-center bg-white/20 rounded-lg px-3 py-2 backdrop-blur-sm">
        <svg className="w-4 h-4 mr-2 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
        </svg>
        {truncatedBreadcrumbs.map((item, index) => (
          <React.Fragment key={index}>
            {index !== 0 && (
              <svg className="w-4 h-4 mx-2 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            )}
            {'isEllipsis' in item ? (
              <span className="text-white/70 font-medium">...</span>
            ) : item.isLast && currentPrefix && !currentPrefix.endsWith('/') ? (
              // If this is the last breadcrumb and we have a filename prefix (no trailing slash),
              // make it clickable to clear the filename prefix but keep it highlighted
              <button
                onClick={() => onNavigateToBreadcrumb(item.path)}
                className="font-semibold text-white bg-white/30 px-2 py-1 rounded hover:bg-white/40 transition-colors"
              >
                {item.name}
              </button>
            ) : item.isLast ? (
              <span className="font-semibold text-white bg-white/30 px-2 py-1 rounded">{item.name}</span>
            ) : (
              <button
                onClick={() => onNavigateToBreadcrumb(item.path)}
                className="text-white hover:opacity-80 transition-colors font-medium"
              >
                {item.name}
              </button>
            )}
          </React.Fragment>
        ))}
      </div>
      <div className="flex items-center space-x-3">
        {/* Loading indicator - always reserve space to prevent layout shifts */}
        <div className="flex items-center space-x-2 px-3 py-2" style={{ 
          opacity: isLoading ? 1 : 0,
          visibility: isLoading ? 'visible' : 'hidden'
        }}>
          <div className="w-5 h-5 border-2 border-orange-200 rounded-full animate-spin" style={{ borderTopColor: '#F96423' }}></div>
          <span className="text-sm font-medium" style={{ color: '#F96423' }}>Loading...</span>
        </div>
        
        {truncatedBreadcrumbs.length > 1 && (
          <button
            onClick={onGoBack}
            className="inline-flex items-center px-6 py-2.5 text-sm font-medium text-white bg-white/20 rounded-xl hover:bg-white/30 transition-all duration-200 backdrop-blur-sm border border-white/30"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back
          </button>
        )}
      </div>
    </div>
  );
};

export default BreadcrumbNavigation;
